import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { debounceTime } from 'rxjs/operators';
import { CompanyMarketplaceService } from '../../../../../core/services/company-marketplaces.service';
import { ConstantService } from '../../../../../core/services/constant.service';
import { UserService } from '../../../../../core/services/user.service';
import { RegistrationService } from '../../../../customer-registration/services/registration.service';
import { GlobalSettingsService } from '../../../../setting/services/global-settings.service';
import { ReportingToolService } from '../../../reporting-tool.service';

export interface IctReportRow {
    transactionDate: string;
    invoiceNumber: string;
    invoiceDate: string;
    currency: string;
    supplierId: string;
    supplierName: string;
    supplierCountry: string;
    supplierVatNumberUsed: string;
    customerId: string;
    customerName: string;
    customerCountry: string;
    customerVatNumberUsed: string;
    taxableBasis: number;
    countryDispatch: string;
    countryArrival: string;
    deliveryConditions: string;
    reportingType: string;
    transactionType: string;
    intrastatCode: string;
    quantity1: number;
    unit1: string;
    quantity2: number;
    unit2: string;
    commercialValue: number;
    statisticalValue: number;
    modeOfTransport: string;
    regionArrival: string;
    countryOrigin: string;
}

const MOCK_ICT_ROWS: IctReportRow[] = [
    {
        transactionDate: '2026-01-15',
        invoiceNumber: 'INV-ICT-1001',
        invoiceDate: '2026-01-14',
        currency: 'EUR',
        supplierId: 'SUP-101',
        supplierName: 'Acme Trading BV',
        supplierCountry: 'NL',
        supplierVatNumberUsed: 'NL123456789B01',
        customerId: 'CUS-201',
        customerName: 'Berlin Retail GmbH',
        customerCountry: 'DE',
        customerVatNumberUsed: 'DE987654321',
        taxableBasis: 1250.00,
        countryDispatch: 'NL',
        countryArrival: 'DE',
        deliveryConditions: 'DAP',
        reportingType: 'Intrastat',
        transactionType: '11',
        intrastatCode: '61091000',
        quantity1: 100,
        unit1: 'PCS',
        quantity2: 25.5,
        unit2: 'KG',
        commercialValue: 1250.00,
        statisticalValue: 1200.00,
        modeOfTransport: '3',
        regionArrival: 'BE',
        countryOrigin: 'CN',
    },
    {
        transactionDate: '2026-02-03',
        invoiceNumber: 'INV-ICT-1002',
        invoiceDate: '2026-02-02',
        currency: 'EUR',
        supplierId: 'SUP-102',
        supplierName: 'Paris Fashion SAS',
        supplierCountry: 'FR',
        supplierVatNumberUsed: 'FR12345678901',
        customerId: 'CUS-202',
        customerName: 'Madrid Goods SL',
        customerCountry: 'ES',
        customerVatNumberUsed: 'ESA12345678',
        taxableBasis: 890.40,
        countryDispatch: 'FR',
        countryArrival: 'ES',
        deliveryConditions: 'CIP',
        reportingType: 'Intrastat',
        transactionType: '11',
        intrastatCode: '62034231',
        quantity1: 45,
        unit1: 'PCS',
        quantity2: 18.2,
        unit2: 'KG',
        commercialValue: 890.40,
        statisticalValue: 850.00,
        modeOfTransport: '3',
        regionArrival: 'MD',
        countryOrigin: 'FR',
    },
    {
        transactionDate: '2026-02-20',
        invoiceNumber: 'INV-ICT-1003',
        invoiceDate: '2026-02-19',
        currency: 'EUR',
        supplierId: 'SUP-101',
        supplierName: 'Acme Trading BV',
        supplierCountry: 'NL',
        supplierVatNumberUsed: 'NL123456789B01',
        customerId: 'CUS-203',
        customerName: 'Brussels Market SA',
        customerCountry: 'BE',
        customerVatNumberUsed: 'BE0123456789',
        taxableBasis: 2100.00,
        countryDispatch: 'NL',
        countryArrival: 'BE',
        deliveryConditions: 'DDP',
        reportingType: 'Intrastat',
        transactionType: '21',
        intrastatCode: '85171200',
        quantity1: 12,
        unit1: 'PCS',
        quantity2: 8.0,
        unit2: 'KG',
        commercialValue: 2100.00,
        statisticalValue: 2050.00,
        modeOfTransport: '3',
        regionArrival: 'BRU',
        countryOrigin: 'NL',
    },
    {
        transactionDate: '2026-03-08',
        invoiceNumber: 'INV-ICT-1004',
        invoiceDate: '2026-03-07',
        currency: 'EUR',
        supplierId: 'SUP-103',
        supplierName: 'Berlin Retail GmbH',
        supplierCountry: 'DE',
        supplierVatNumberUsed: 'DE987654321',
        customerId: 'CUS-204',
        customerName: 'Acme Trading BV',
        customerCountry: 'NL',
        customerVatNumberUsed: 'NL123456789B01',
        taxableBasis: 450.75,
        countryDispatch: 'DE',
        countryArrival: 'NL',
        deliveryConditions: 'EXW',
        reportingType: 'Intrastat',
        transactionType: '11',
        intrastatCode: '94036090',
        quantity1: 5,
        unit1: 'PCS',
        quantity2: 42.0,
        unit2: 'KG',
        commercialValue: 450.75,
        statisticalValue: 430.00,
        modeOfTransport: '3',
        regionArrival: 'NH',
        countryOrigin: 'DE',
    },
    {
        transactionDate: '2026-03-22',
        invoiceNumber: 'INV-ICT-1005',
        invoiceDate: '2026-03-21',
        currency: 'EUR',
        supplierId: 'SUP-104',
        supplierName: 'Madrid Goods SL',
        supplierCountry: 'ES',
        supplierVatNumberUsed: 'ESA12345678',
        customerId: 'CUS-205',
        customerName: 'Paris Fashion SAS',
        customerCountry: 'FR',
        customerVatNumberUsed: 'FR12345678901',
        taxableBasis: 1675.20,
        countryDispatch: 'ES',
        countryArrival: 'FR',
        deliveryConditions: 'FCA',
        reportingType: 'Intrastat',
        transactionType: '11',
        intrastatCode: '39269097',
        quantity1: 200,
        unit1: 'PCS',
        quantity2: 55.0,
        unit2: 'KG',
        commercialValue: 1675.20,
        statisticalValue: 1600.00,
        modeOfTransport: '4',
        regionArrival: 'IDF',
        countryOrigin: 'ES',
    },
];

@Component({
    selector: 'eci-ict-reports',
    templateUrl: './ict-reports.component.html',
    styleUrls: ['./ict-reports.component.scss'],
    providers: [DatePipe],
})
export class IctReportsComponent implements OnInit {

    form: FormGroup;
    showLoader = false;

    countriesAdmin: any[] = [];
    displayedCountries: any[] = [];
    companyList: any[] = [];

    displayedColumns: string[] = [
        'transactionDate',
        'invoiceNumber',
        'invoiceDate',
        'currency',
        'supplierId',
        'supplierName',
        'supplierCountry',
        'supplierVatNumberUsed',
        'customerId',
        'customerName',
        'customerCountry',
        'customerVatNumberUsed',
        'taxableBasis',
        'countryDispatch',
        'countryArrival',
        'deliveryConditions',
        'reportingType',
        'transactionType',
        'intrastatCode',
        'quantity1',
        'unit1',
        'quantity2',
        'unit2',
        'commercialValue',
        'statisticalValue',
        'modeOfTransport',
        'regionArrival',
        'countryOrigin',
    ];
    dataSource = new MatTableDataSource<IctReportRow>([]);

    constructor(
        private formBuilder: FormBuilder,
        private datePipe: DatePipe,
        public userSvc: UserService,
        private constSvc: ConstantService,
        private regSvc: RegistrationService,
        private companyMarketplaceSvc: CompanyMarketplaceService,
        private globalSettingsSvc: GlobalSettingsService,
        public reportingToolService: ReportingToolService,
    ) {
    }

    ngOnInit(): void {
        this.reportingToolService.selectedCompany = null;
        this.reportingToolService.selectedShop = null;
        this.reportingToolService.selectedCountry = null;

        this.form = this.initializeForm();

        this.form.get('searchCustomerName').valueChanges.pipe(debounceTime(500)).subscribe(() => {
            this.getLists();
        });

        if (this.userSvc.isAdmin) {
            this.globalSettingsSvc.getVatDetailsByCompany(this.userSvc.company.id).subscribe(res => {
                this.countriesAdmin = (res?.items || []).map(r => ({
                    ...r.country,
                    vatNumber: r.vatNumber,
                    eoriNumber: r.eoriNumber,
                }));
                this.displayedCountries = this.countriesAdmin;
            });
            this.getListCompanies();
        } else {
            this.getListShops();
            this.constSvc.getAvailableCountries(1, 1000, true, true).subscribe(data => {
                this.displayedCountries = data || [];
            });
        }
    }

    get isAdmin(): boolean {
        return this.userSvc.isAdmin;
    }

    get filteredCountries(): any[] {
        const search = (this.form?.get('searchCountry')?.value || '').toLowerCase();
        if (!search) {
            return this.displayedCountries || [];
        }
        return (this.displayedCountries || []).filter(c =>
            (c.name || '').toLowerCase().startsWith(search)
        );
    }

    initializeForm(): FormGroup {
        const today = new Date();
        const dateFrom = new Date(today.getFullYear(), 0, 1);
        const dateTo = new Date(this.datePipe.transform(today, 'yyyy-MM-dd'));

        return this.formBuilder.group({
            dateFrom: [dateFrom],
            dateTo: [dateTo],
            customerName: [null],
            searchCustomerName: [''],
            countryCode: [null],
            searchCountry: [''],
        });
    }

    getCountryNameByCode(value: any): string {
        return this.displayedCountries.find(c => c.code === value)?.name || '';
    }

    getFlagClass(code: string): string {
        return `iti__${code?.toLowerCase()}`;
    }

    onSelectCountry(e): void {
        this.reportingToolService.selectedCountry = this.displayedCountries.find(c => c.code === e.value) || null;
    }

    onSelectCustomer(e): void {
        if (this.userSvc.isAdmin) {
            this.form.get('countryCode')?.setValue(null);
            this.reportingToolService.selectedCountry = null;
            this.reportingToolService.selectedCompany = this.companyList.find(c => c.id == e.value) || null;
        } else {
            this.reportingToolService.selectedShop = this.companyList.find(c => c.id == e.value) || null;
        }
    }

    getLists(): void {
        if (this.userSvc.isAdmin) {
            this.getListCompanies();
        } else {
            this.getListShops();
        }
    }

    getListCompanies(q: string = this.form?.get('searchCustomerName').value): void {
        this.regSvc.getCompanyListForRepTool(q, false).subscribe(data => {
            if (!data) {
                return;
            }
            this.companyList = data;
        });
    }

    getListShops(q: string = this.form?.controls?.searchCustomerName.value): void {
        this.companyMarketplaceSvc.getListOfShops(this.userSvc.company.id, q).subscribe(data => {
            this.companyList = data;
        });
    }

    clearFilters(): void {
        const today = new Date();
        this.form.get('dateFrom').setValue(new Date(today.getFullYear(), 0, 1));
        this.form.get('dateTo').setValue(this.datePipe.transform(today, 'yyyy-MM-dd'));
        this.form.get('countryCode').setValue(null);
        this.form.get('customerName').setValue('');
        this.form.get('searchCustomerName').setValue('');
        this.form.get('searchCountry').setValue('');
        this.reportingToolService.selectedCompany = null;
        this.reportingToolService.selectedShop = null;
        this.reportingToolService.selectedCountry = null;
        this.dataSource = new MatTableDataSource<IctReportRow>([]);
    }

    formSubmit(): void {
        this.dataSource = new MatTableDataSource<IctReportRow>([]);
    }
}
